import type { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, FormControl, IconButton, Skeleton, Typography, styled } from '@mui/material';
import { AltRoute as AltRouteIcon, Delete as DeleteIcon } from '@mui/icons-material';
import type { ActionIndexProps } from '../types';
import RuleCommonAutocomplete from './RuleCommonAutocomplete';
import type { Option } from 'types/shared';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import Autocomplete from 'components/common/Autocomplete';
import { isEmpty } from 'lodash-es';
import useCurrentTasksFormStore from '../hooks/useCurrentTasksFormStore';
import useRulePageMode from '../hooks/useRulePageMode';
import { notificationChannelOptions } from '../api/getNotificationChannels';
import { useSuspenseQuery } from '@tanstack/react-query';

export const SuspenseFallbackSkeleton = () => (
  <Box sx={{ p: 0.5 }}>
    <Skeleton
      width={'100%'}
      height={26}
      sx={(t) => ({
        transform: 'translateY(0)',
        m: 0,
        borderRadius: t.borderRadius.md,
      })}
    />
  </Box>
);

export const RuleEditAutoCompleteStyled = styled(RuleCommonAutocomplete)({
  '& .MuiInputBase-root': { minHeight: 36, maxHeight: 36, border: 'none' },
  '& .MuiOutlinedInput-root fieldset': { border: 'none', minHeight: 36, maxHeight: 36, top: 0 },
  '&.MuiAutocomplete-listbox ': {
    wordBreak: 'break-all',
  },
  '& .MuiAutocomplete-option': {
    padding: '6px 16px',
    width: '100%',
    wordBreak: 'break-all',
    lineHeight: 1.25,
    boxSizing: 'border-box',
  },
});

export const DeleteAction: FC<ActionIndexProps> = observer(({ indexTask, indexVariant, indexAction }) => {
  const { onShowAlert, onShowInfoAlert } = useEnqueueSnackbar();
  const tasksFormStore = useCurrentTasksFormStore();

  const handleDelete = () => {
    const handleDeletion = tasksFormStore.deleteAction({ indexTask, indexVariant, indexAction });

    onShowAlert('Action deleted', {
      delayAlert: 3500,
      onUndo: () => {
        handleDeletion.undo({ indexTask, indexVariant });
        onShowInfoAlert('Action recovered', {
          isPending: false,
          delayAlert: 3500,
        });
      },
    });
  };

  return (
    <IconButton size="medium" sx={{ p: 0, height: 24 }} onClick={handleDelete} className="visiblyDeleteButton">
      <DeleteIcon sx={{ fontSize: 24, mb: -0.3 }} />
    </IconButton>
  );
});

export const ActionFormContainerTitle: FC<
  ActionIndexProps & {
    title: string;
  }
> = ({ title, indexTask, indexVariant, indexAction }) => {
  const { isRead } = useRulePageMode();
  const isVariant = typeof indexVariant === 'number';
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: isRead ? 'start' : 'space-between', flexGrow: 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          <AltRouteIcon sx={(t) => ({ color: t.palette.primary.main, fontSize: t.typography.h4 })} />
          <Typography variant="subtitle2" sx={(t) => ({ color: t.palette.primary.main, fontWeight: 500 })}>
            {title}
          </Typography>
        </Box>
      </Box>
      {!isRead && !isVariant && (
        <DeleteAction indexTask={indexTask} indexAction={indexAction} indexVariant={indexVariant} />
      )}
    </Box>
  );
};

interface NotificationChannelsAutocompleteInputProps {
  label?: string;
  inputLabel?: string;
  name: string;
  required?: boolean;
}

export const NotificationChannelsAutocomplete: FC<NotificationChannelsAutocompleteInputProps> = ({
  name,
  label,
  inputLabel,
  required,
}) => {
  const { isRead } = useRulePageMode();
  const { data: notificationChannels } = useSuspenseQuery({
    ...notificationChannelOptions,
    select: (data): Option[] => {
      return data.map(({ id, name }) => ({ value: id, label: name }));
    },
  });

  return (
    <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
      {label && !inputLabel && (
        <Typography variant="subtitle2" mb={1}>
          {label}
          {required && '*'}
        </Typography>
      )}
      <FormControl sx={{ flex: 1 }}>
        <Autocomplete
          InputProps={{ disabled: isEmpty(notificationChannels) || isRead, label: inputLabel ?? '' }}
          options={notificationChannels ?? []}
          allSelectable={false}
          name={name}
          id={name}
          label={inputLabel}
          innerLabel
        />
      </FormControl>
    </Box>
  );
};
